// src/components/FileManagerPage.js

import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebaseConfig';
import { collection, query, where, orderBy, onSnapshot, doc, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';

function FileManagerPage({ userId }) {
  const [audioFiles, setAudioFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null); // For modal

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    // Query Firestore for audio files belonging to the current user
    // Note: Firestore orderBy() can require indexes. For simplicity,
    // we'll sort in memory if needed, or rely on default order.
    // For production, consider adding Firestore indexes for `timestamp` or `title`.
    const q = query(
      collection(db, `users/${userId}/audioFiles`),
      // orderBy("timestamp", "desc") // Uncomment if you add an index for timestamp
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const files = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort in memory if orderBy is not used in query
      files.sort((a, b) => b.timestamp - a.timestamp);
      setAudioFiles(files);
      setLoading(false);
    }, (err) => {
      console.error("Error fetching audio files:", err);
      setError("Failed to load audio files. Please try again.");
      setLoading(false);
    });

    return () => unsubscribe(); // Cleanup listener
  }, [userId]);

  const handleDelete = async (fileId, storagePath) => {
    if (!window.confirm("Are you sure you want to delete this file?")) {
      return;
    }

    try {
      // 1. Delete from Cloud Storage
      const fileRef = ref(storage, storagePath);
      await deleteObject(fileRef);

      // 2. Delete from Firestore
      await deleteDoc(doc(db, `users/${userId}/audioFiles`, fileId));

      console.log(`File ${fileId} deleted successfully.`);
    } catch (err) {
      console.error("Error deleting file:", err);
      setError(`Failed to delete file: ${err.message}`);
    }
  };

  const openDetailsModal = (file) => {
    setSelectedFile(file);
  };

  const closeDetailsModal = () => {
    setSelectedFile(null);
  };

  if (loading) {
    return <div className="text-center text-gray-600 text-lg mt-8">Loading files...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mt-6" role="alert">
        <p className="font-bold">Error!</p>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-xl shadow-2xl">
      <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">My Generated Audio Files</h2>

      {audioFiles.length === 0 ? (
        <p className="text-center text-gray-600 text-lg">No audio files generated yet. Go to a TTS page to create some!</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">Title</th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">API Used</th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">Generated On</th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {audioFiles.map((file) => (
                <tr key={file.id} className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50">
                  <td className="py-3 px-4 text-gray-800">{file.title || 'Untitled'}</td>
                  <td className="py-3 px-4 text-gray-800">{file.apiUsed}</td>
                  <td className="py-3 px-4 text-gray-800">
                    {new Date(file.timestamp).toLocaleString()}
                  </td>
                  <td className="py-3 px-4 flex space-x-2">
                    <a
                      href={file.audioUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-blue-500 hover:bg-blue-600 text-white text-sm font-bold py-2 px-3 rounded-lg shadow-md transition duration-300"
                    >
                      Download
                    </a>
                    <button
                      onClick={() => openDetailsModal(file)}
                      className="bg-gray-500 hover:bg-gray-600 text-white text-sm font-bold py-2 px-3 rounded-lg shadow-md transition duration-300"
                    >
                      Details
                    </button>
                    <button
                      onClick={() => handleDelete(file.id, file.storagePath)}
                      className="bg-red-500 hover:bg-red-600 text-white text-sm font-bold py-2 px-3 rounded-lg shadow-md transition duration-300"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* File Details Modal */}
      {selectedFile && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">File Details: {selectedFile.title || 'Untitled'}</h3>
            <div className="space-y-3 text-gray-700">
              <p><strong>API Used:</strong> {selectedFile.apiUsed}</p>
              <p><strong>Generated On:</strong> {new Date(selectedFile.timestamp).toLocaleString()}</p>
              <p><strong>Audio URL:</strong> <a href={selectedFile.audioUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{selectedFile.audioUrl}</a></p>
              <p><strong>Original Text:</strong></p>
              <div className="bg-gray-100 p-4 rounded-lg border border-gray-200 max-h-40 overflow-y-auto">
                <p className="whitespace-pre-wrap text-sm">{selectedFile.originalText}</p>
              </div>
              <p><strong>Generation Parameters:</strong></p>
              <div className="bg-gray-100 p-4 rounded-lg border border-gray-200 max-h-40 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(selectedFile.params, null, 2)}</pre>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={closeDetailsModal}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FileManagerPage;